import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { clearWelcomeGreetingFlag } from '../lib/welcomeGreeting';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('aerta_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      api.setToken(token);
      const userData = await api.get('/api/auth/me');
      setUser(userData);
    } catch {
      localStorage.removeItem('aerta_token');
      api.setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    const token = response.access_token || response.token;
    if (!token) {
      throw new Error('Login succeeded but token was missing in response.');
    }
    api.setToken(token);
    const me = response.user || (await api.get('/api/auth/me').catch(() => null));
    setUser(me || { email });
    return response;
  };

  const register = async (email, password, fullName, inviteCode) => {
    const body = {
      email,
      password,
      full_name: fullName,
    };
    if (inviteCode && String(inviteCode).trim()) {
      body.invite_code = String(inviteCode).trim();
    }
    const response = await api.post('/api/auth/register', body);
    const token = response.access_token || response.token;
    if (!token) {
      throw new Error('Registration succeeded but token was missing in response.');
    }
    api.setToken(token);
    const me = response.user || (await api.get('/api/auth/me').catch(() => null));
    setUser(me || { email });
    return response;
  };

  const logout = () => {
    clearWelcomeGreetingFlag();
    api.setToken(null);
    setUser(null);
  };

  const requestMagicLink = useCallback(async (emailAddr) => {
    return api.post('/api/auth/magic-link/request', { email: emailAddr });
  }, []);

  const consumeMagicLink = useCallback(async (token) => {
    const response = await api.post('/api/auth/magic-link/consume', { token });
    const tok = response.access_token || response.token;
    if (!tok) throw new Error('Sign-in link response missing token.');
    api.setToken(tok);
    let me = null;
    try {
      me = await api.get('/api/auth/me');
    } catch {
      me = response.user || null;
    }
    setUser(me || response.user || null);
    return response;
  }, []);

  const submitPilotFeedback = useCallback(async (rating, comment) => {
    await api.post('/api/pilot/feedback', {
      rating: rating || undefined,
      comment: comment || undefined,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        checkAuth,
        requestMagicLink,
        consumeMagicLink,
        submitPilotFeedback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
